import { db } from "@/main";
import { firestore } from "firebase";
import { getClientId } from "./ClientRepository";
import { ClientModule } from "@/store/modules/ClientModule";

const ADMIN_CONFIG_REF = "administration/adminConfig";

/**
 * Session cache of the `administration/adminConfig` document.
 *
 * Both admin allow-lists live in that one document and the route guards consult them on every
 * guarded navigation, so without this each navigation costs a Firestore read. We deliberately
 * cache the **document** rather than the resolved boolean: the client id is read fresh on every
 * check, so a master/member client switch is picked up without invalidating anything.
 *
 * Call `clearAdminConfigCache()` after writing the document to force a re-read.
 */
let adminConfigPromise: Promise<firestore.DocumentData | null> | null = null;

function getAdminConfig(): Promise<firestore.DocumentData | null> {
  if (!adminConfigPromise) {
    adminConfigPromise = db
      .doc(ADMIN_CONFIG_REF)
      .get()
      .then((docSnap) => (docSnap.exists ? docSnap.data() ?? null : null))
      .catch(() => {
        // Never cache a failure: a transient read error would otherwise lock the session out
        // of every admin surface until the page is reloaded.
        adminConfigPromise = null;
        return null;
      });
  }

  return adminConfigPromise;
}

/** Drops the cached admin config so the next check re-reads it. */
export function clearAdminConfigCache() {
  adminConfigPromise = null;
}

/**
 * Whether the signed-in client appears in the given allow-list field of `adminConfig`.
 *
 * Missing field or missing document means "not allowed", so a new allow-list can be introduced
 * in code before the field exists in Firestore.
 */
async function isClientInAdminAllowList(field: string): Promise<boolean> {
  const clientId = getClientId();

  if (!clientId) {
    return false;
  }

  const allowList = ((await getAdminConfig())?.[field] as string[] | undefined) ?? [];

  return allowList.includes(clientId);
}

/**
 * Checks whether the current client is an IRES system admin client.
 *
 * System admin clients are listed in `administration/adminConfig.adminClientIds[]`. This is the
 * **IRES Admin Android app's** allow-list, and in the Firestore security rules it confers firmware,
 * equipment, batch and delete rights. Do not reuse it to gate Office admin-portal access — use
 * `isOfficeAdminClient()`, which is deliberately a separate list.
 */
export async function isIresSystemAdminClient(): Promise<boolean> {
  return isClientInAdminAllowList("adminClientIds");
}

/**
 * Checks whether the current client may enter the Office admin portal.
 *
 * Clients are listed in `administration/adminConfig.officeAdminClientIds[]`, kept separate from
 * `adminClientIds` so that Android admin powers and Office portal access can be granted
 * independently.
 *
 * This is the **client** layer only. It says nothing about what the individual user may do inside
 * the portal: the admin client is `clientType: "IRES"`, so all of its users already hold the full
 * IRES capability set. Anything that must be restricted to particular admin users has to be gated
 * on a user capability as well, never on portal membership alone.
 */
export async function isOfficeAdminClient(): Promise<boolean> {
  return isClientInAdminAllowList("officeAdminClientIds");
}

/**
 * Resolves admin-portal membership and caches it for synchronous readers.
 *
 * `isOfficeAdminClient()` is async, but the navigation, the settings page and the route guards all
 * need the answer while rendering. This writes the resolved value into `ClientModule`, from where
 * `isOfficeAdminPortalClient()` (`src/permissions/ClientPermissions.ts`) reads it synchronously.
 *
 * Call it wherever the signed-in client can change: after login, on a master/member client switch,
 * and on each guarded navigation, so the cache cannot go stale.
 *
 * @returns The resolved value, so callers that need it immediately can await this instead.
 */
export async function resolveOfficeAdminPortalClient(): Promise<boolean> {
  const isAdminPortalClient = await isOfficeAdminClient();

  ClientModule.setIsOfficeAdminPortalClient(isAdminPortalClient);

  return isAdminPortalClient;
}

export function getApksRef() {
  return "administration/apks";
}

export async function getLastHundredApkVersions() {
  const docSnap = await db.doc(getApksRef()).get();
  if (!docSnap.exists) return [];

  const fileNamesMap = (docSnap.data()?.fileNames as Record<string, firestore.Timestamp>) || {};
  const sortedFileNames = Object.entries(fileNamesMap)
    .sort(([, aTimestamp], [, bTimestamp]) => bTimestamp.toMillis() - aTimestamp.toMillis())
    .map(([fileName]) => fileName)
    .slice(0, 100);

  return sortedFileNames;
}

export function getDryingInfoConfigsRef() {
  return "administration/roomInfoConfig";
}

export async function getLastHundredDryingInfoConfigVersions() {
  const docSnap = await db.doc(getDryingInfoConfigsRef()).get();
  if (!docSnap.exists) return [];

  const versions = (docSnap.data()?.versionCodes as string[]) || [];
  const sortedVersions = versions.sort((a, b) => {
    const aParts = a.split(".").map(Number);
    const bParts = b.split(".").map(Number);

    // Pad shorter arrays to equal length
    const len = Math.max(aParts.length, bParts.length);
    while (aParts.length < len) aParts.push(0);
    while (bParts.length < len) bParts.push(0);

    // Compare each segment numerically
    for (let i = 0; i < len; i++) {
      if (aParts[i] !== bParts[i]) {
        return bParts[i] - aParts[i]; // descending order
      }
    }
    return 0;
  })
  .slice(0, 100);

  return sortedVersions;
}
